// A WebSocket client for the "Cue" realtime signaling protocol (see
// wire.ts for the wire format, crypto.ts for the crypto, and
// backend/README.md's "Cue / WebSocket signaling protocol" section for the
// full server-side story). Browsers can't open raw UDP sockets, so this
// talks to the same backend over its WebSocket transport
// (backend/protocol/ws.go) instead of UDP - iOS's CueClient.swift uses UDP,
// but both transports share one connected-client/room registry on the
// server (see cmd/protocol/main.go), so a message sent from here relays to
// an iOS client in the same room and vice versa.
import {
  buildEnvelope,
  buildPayload,
  buildTemplateSignalValue,
  buildReplyValue,
  withMessageId,
  parseIncoming,
  isPlaintextStatusReply,
  CueCommand,
  DEFAULT_GROUP_NO,
  DEFAULT_OPERATION,
  type CueRelayedMessage,
  type CueReply,
} from './wire';
import { encryptBase64, decryptBase64 } from './crypto';
import { setCueConnectionState, recordCueHeartbeatAck, recordCueEvent, setCueRoomState, clearCueRoomState, clearAllCueRooms } from './diagnostics';
import { appLog, appWarn } from '../logger';

export class CueClientError extends Error {}

// Shares VITE_BACKEND_HOST with vite.config.ts's /api-auth proxy target -
// one knob for the real backend's host, see .env.example. Production is
// reverse-proxied at api.zmist.co with real TLS and no raw port, so this
// derives a wss:// URL, no port. VITE_CUE_WS_URL still overrides this
// fully if a deployment doesn't fit that pattern.
const DEFAULT_BACKEND_HOST = import.meta.env.VITE_BACKEND_HOST ?? 'api.zmist.lo';
const DEFAULT_WS_URL = import.meta.env.VITE_CUE_WS_URL ?? `wss://${DEFAULT_BACKEND_HOST}/`;

interface PendingRequest {
  resolve: (reply: CueReply) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  // All three added for lib/cue/diagnostics.ts's benefit only - command to
  // recognize a heartbeat reply specifically and to describe the reply in
  // words (see handleIncoming's reply branch), channelId for the same
  // reason, sentAt to compute the round-trip time reported there.
  command: string;
  channelId: string;
  sentAt: number;
}

// How often to send a session heartbeat ("su") while connected. Purely a
// keepalive - the server doesn't require it (a connection can join a room
// or send a message without ever opening a session first). gorilla/
// websocket (backend/protocol/ws.go) sets no read deadline of its own, but
// most reverse proxies/load balancers in front of a WS endpoint do have an
// idle-connection timeout (commonly ~60s) and will silently drop a
// connection that's gone quiet in both directions - the browser doesn't
// always notice immediately (a half-closed TCP connection can look "OPEN"
// client-side for a while), so a request sent right after that just times
// out with no explicit close event ever firing. 20s stays comfortably
// under the shortest commonly-seen idle timeout. See CueClient.swift's
// identical heartbeatInterval for the UDP/NAT-mapping equivalent of this
// same problem on iOS.
const HEARTBEAT_INTERVAL_MS = 20_000;

function roomKey(channelId: string, groupNo: string): string {
  return `${channelId}:${groupNo}`;
}

interface CueClientConfig {
  securityKey: string;
  secret: string;
  url?: string;
}

export class CueClient {
  private ws: WebSocket | null = null;
  private connecting: Promise<void> | null = null;
  private messageCounter = 0;
  private joinedRooms = new Set<string>();
  private pending = new Map<string, PendingRequest>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  // Auto-reconnect state. A socket that drops while joinedRooms is
  // non-empty (i.e. someone still cares about live updates) is redialed
  // on a capped backoff, and every room is rejoined afterwards - the
  // server tracks room membership per connection, so a fresh socket has
  // joined nothing until it re-sends "c" (mirrors the C/Go daemons'
  // rejoin-on-reconnect). deliberateClose suppresses all of this for an
  // intentional disconnect() (logout, credential change).
  private deliberateClose = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private static readonly RECONNECT_MIN_MS = 1_000;
  private static readonly RECONNECT_MAX_MS = 30_000;

  /**
   * Handlers invoked (all of them) whenever another client relays a
   * message into a room this client has joined. Register with
   * addRelayHandler; it returns an unsubscribe function. Several can be
   * active at once - one persistent app-wide handler that refetches
   * whatever channel a relay names, plus a per-screen one for the open
   * channel, say.
   */
  private relayHandlers = new Set<(relayed: CueRelayedMessage) => void>();

  addRelayHandler(handler: (relayed: CueRelayedMessage) => void): () => void {
    this.relayHandlers.add(handler);
    return () => {
      this.relayHandlers.delete(handler);
    };
  }

  constructor(config: CueClientConfig) {
    this.securityKey = config.securityKey;
    this.secret = config.secret;
    this.url = config.url ?? DEFAULT_WS_URL;
  }

  private readonly securityKey: string;
  private readonly secret: string;
  private readonly url: string;

  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.connecting) return this.connecting;
    // A fresh connect() attempt (including the reconnect path) is by
    // definition not a deliberate close.
    this.deliberateClose = false;

    appLog(`[Cue] connecting to ${this.url}`);
    setCueConnectionState('connecting');
    this.connecting = new Promise<void>((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(this.url);
      this.ws = ws;

      ws.onopen = () => {
        if (settled) return;
        settled = true;
        this.connecting = null;
        this.reconnectAttempts = 0;
        appLog('[Cue] connection open');
        setCueConnectionState('connected');
        this.startHeartbeat();
        resolve();
      };
      ws.onerror = (event) => {
        appWarn('[Cue] connection error', event);
        if (settled) return;
        settled = true;
        this.connecting = null;
        setCueConnectionState('disconnected');
        reject(new CueClientError('Could not connect to the signaling server.'));
      };
      ws.onclose = (event) => {
        appWarn(`[Cue] connection closed (code=${event.code}, reason=${event.reason || '(none)'}, clean=${event.wasClean})`);
        setCueConnectionState('disconnected');
        this.stopHeartbeat();
        if (!settled) {
          settled = true;
          this.connecting = null;
          reject(new CueClientError('Could not connect to the signaling server.'));
        }
        if (this.ws === ws) this.ws = null;
        // joinedRooms is deliberately NOT cleared here - a dropped socket
        // should come back joined to the same rooms (see scheduleReconnect
        // / rejoinAll). disconnect() clears it for an intentional close.
        for (const request of this.pending.values()) {
          clearTimeout(request.timer);
          request.reject(new CueClientError('Connection closed.'));
        }
        this.pending.clear();
        if (!this.deliberateClose) this.scheduleReconnect();
      };
      ws.onmessage = (event) => {
        if (typeof event.data === 'string') void this.handleIncoming(event.data);
      };
    });
    return this.connecting;
  }

  disconnect(): void {
    setCueConnectionState('disconnected');
    this.deliberateClose = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.stopHeartbeat();
    this.ws?.close();
    this.ws = null;
    this.joinedRooms.clear();
    clearAllCueRooms();
    this.relayHandlers.clear();
  }

  /** See HEARTBEAT_INTERVAL_MS's doc comment for why this exists. */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.request(CueCommand.sessionHeartbeat, '_', '_').catch((error) => {
        // A heartbeat that never gets a reply usually means a half-open
        // socket the browser hasn't noticed (see HEARTBEAT_INTERVAL_MS) -
        // force it closed so onclose fires and the reconnect path takes
        // over, rather than sitting on a dead connection until the next
        // send times out too. Matches the daemons' reconnect-on-missed-
        // heartbeat behaviour.
        appWarn('[Cue] heartbeat failed, dropping the connection to reconnect', error);
        this.ws?.close();
      });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    if (this.deliberateClose) return;
    if (this.joinedRooms.size === 0) return; // nothing left that needs a live connection

    const delay = Math.min(
      CueClient.RECONNECT_MIN_MS * 2 ** this.reconnectAttempts,
      CueClient.RECONNECT_MAX_MS
    );
    this.reconnectAttempts += 1;
    appLog(`[Cue] scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.reconnectAndRejoin();
    }, delay);
  }

  /**
   * Manual reconnect - e.g. the user clicking a red/amber connection dot
   * in the channel list instead of waiting out scheduleReconnect's current
   * backoff. Cancels any pending backoff timer and resets the attempt
   * counter so the next drop starts its own backoff from scratch, then
   * runs the exact same connect+rejoin path a scheduled reconnect would.
   * connect() itself already no-ops if the socket is open, and rejoinAll
   * re-sending "c" for an already-joined room is harmless - so this is
   * safe to call from any state (disconnected, still connecting/joining,
   * or already fully live) without any special-casing here.
   */
  async forceReconnect(): Promise<void> {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    await this.reconnectAndRejoin();
  }

  private async reconnectAndRejoin(): Promise<void> {
    if (this.deliberateClose) return;
    try {
      await this.connect();
      const allRejoined = await this.rejoinAll();
      if (!allRejoined && !this.deliberateClose) {
        // Some room didn't come back - don't leave the client believing
        // it's fully live. Another pass on the backoff will retry it.
        this.scheduleReconnect();
      }
    } catch (error) {
      appWarn('[Cue] reconnect attempt failed', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Re-sends "c" for every room in joinedRooms - see deliberateClose's
   * comment. A room the server *rejects* (403 - membership revoked while
   * we were away) is dropped from joinedRooms so we stop claiming it and
   * stop trying to rejoin it every reconnect. A room that fails
   * transiently (timeout, non-202 that isn't a rejection) is kept and
   * surfaced to the caller as `false`, so reconnectAndRejoin can schedule
   * another attempt rather than sitting on a connection that's only
   * half-rejoined - the bug that made live messages silently stop until a
   * full page reload.
   */
  private async rejoinAll(): Promise<boolean> {
    const rooms = [...this.joinedRooms];
    let rejoined = 0;
    let allOk = true;
    for (const room of rooms) {
      const idx = room.lastIndexOf(':');
      const channelId = room.slice(0, idx);
      const groupNo = room.slice(idx + 1);
      try {
        setCueRoomState(channelId, 'joining');
        const reply = await this.request(CueCommand.channelJoin, channelId, groupNo);
        if (reply.status === 202) {
          rejoined += 1;
          setCueRoomState(channelId, 'joined');
        } else if (reply.status === 403) {
          appWarn(`[Cue] rejoin ${room} rejected (403) - dropping the room`);
          this.joinedRooms.delete(room);
          clearCueRoomState(channelId);
        } else {
          appWarn(`[Cue] rejoin ${room} got status ${reply.status} - will retry`);
          allOk = false;
        }
      } catch (error) {
        appWarn(`[Cue] rejoin ${room} failed - will retry`, error);
        allOk = false;
      }
    }
    if (rooms.length > 0) appLog(`[Cue] reconnected, rejoined ${rejoined}/${rooms.length} room(s)`);
    return allOk;
  }

  async joinChannel(channelId: string, groupNo: string = DEFAULT_GROUP_NO): Promise<void> {
    setCueRoomState(channelId, 'joining');
    let reply: CueReply;
    try {
      reply = await this.request(CueCommand.channelJoin, channelId, groupNo);
    } catch (error) {
      clearCueRoomState(channelId);
      throw error;
    }
    if (reply.status !== 202) {
      clearCueRoomState(channelId);
      throw new CueClientError(`Cue server returned status ${reply.status}.`);
    }
    this.joinedRooms.add(roomKey(channelId, groupNo));
    setCueRoomState(channelId, 'joined');
  }

  /**
   * Sends a message into a channel, joining it first if this client hasn't
   * already. The server derives the room straight from the message now and
   * re-asserts the sender's membership on send (triage_message.wsl's
   * HandleMessage), so this pre-join is belt-and-suspenders rather than
   * strictly required. id is the same id
   * the caller's own optimistic local message already has - prepended
   * onto the wire value (wire.ts's withMessageId) so the storage stage
   * (triage_message.wsl -> persist_message.wsl) persists under this exact
   * id instead of minting a fresh one, and this call is now what actually
   * persists the message - see StoreContext.tsx's sendMessage for the
   * REST-fallback-only-on-failure flow this is part of.
   */
  async sendMessage(id: string, text: string, channelId: string, groupNo: string = DEFAULT_GROUP_NO): Promise<void> {
    if (!this.joinedRooms.has(roomKey(channelId, groupNo))) {
      await this.joinChannel(channelId, groupNo);
    }
    // diagnosticValue (request()'s 7th param) stays the plain text, so
    // the Diagnostics screen's event log keeps describing this in words
    // ("Sent message to X: ...") instead of leaking the id prefix into
    // what's displayed - see lib/cue/diagnostics.ts's describeCueEvent.
    const reply = await this.request(CueCommand.message, channelId, groupNo, DEFAULT_OPERATION, withMessageId(id, text), undefined, text);
    if (reply.status !== 202) throw new CueClientError(`Cue server returned status ${reply.status}.`);
  }

  /**
   * Sends a reply into a channel - an ordinary message whose value
   * additionally carries the id of the message it's anchored to (see
   * wire.ts's buildReplyValue and backend/protocol/wire.go's
   * SplitReplyValue). id - see sendMessage's identical comment.
   */
  async sendReply(id: string, text: string, replyToId: string, channelId: string, groupNo: string = DEFAULT_GROUP_NO): Promise<void> {
    if (!this.joinedRooms.has(roomKey(channelId, groupNo))) {
      await this.joinChannel(channelId, groupNo);
    }
    const replyValue = buildReplyValue(replyToId, text);
    const reply = await this.request(CueCommand.reply, channelId, groupNo, DEFAULT_OPERATION, withMessageId(id, replyValue), undefined, text);
    if (reply.status !== 202) throw new CueClientError(`Cue server returned status ${reply.status}.`);
  }

  /**
   * Triggers a template signal live by its number instead of relaying free
   * text - the server resolves the preset and relays its own name/emoji/
   * value (see backend/workflows/protocol/triage_message.wsl's "mt"
   * branch), so other clients see the real thing even though this call
   * never spelled it out. valueOverride matches SignalPayload.value's "use
   * the preset's own default if omitted" convention. replyToId - see
   * buildTemplateSignalValue's comment: lets a signal tap itself be a
   * reply. id - see sendMessage's identical comment.
   */
  async sendTemplateSignal(
    id: string,
    number: string,
    channelId: string,
    valueOverride?: string,
    groupNo: string = DEFAULT_GROUP_NO,
    replyToId?: string
  ): Promise<void> {
    if (!this.joinedRooms.has(roomKey(channelId, groupNo))) {
      await this.joinChannel(channelId, groupNo);
    }
    const displayValue = buildTemplateSignalValue(number, valueOverride, replyToId);
    const reply = await this.request(CueCommand.templateSignal, channelId, groupNo, DEFAULT_OPERATION, withMessageId(id, displayValue), undefined, displayValue);
    if (reply.status !== 202) throw new CueClientError(`Cue server returned status ${reply.status}.`);
  }

  /**
   * Sends a reveal-round control action ("open"/"reveal"/"hide"/"close")
   * live - the server owner-gates it, persists a type "round" marker, and
   * relays it to the room so every open client refetches and re-derives
   * the phase (see backend/workflows/protocol/triage_message.wsl's
   * HandleRound). No message id: the client doesn't render round markers
   * optimistically.
   */
  async sendRound(action: string, channelId: string, groupNo: string = DEFAULT_GROUP_NO): Promise<void> {
    if (!this.joinedRooms.has(roomKey(channelId, groupNo))) {
      await this.joinChannel(channelId, groupNo);
    }
    const reply = await this.request(CueCommand.round, channelId, groupNo, DEFAULT_OPERATION, action, undefined, action);
    if (reply.status !== 202) throw new CueClientError(`Cue server returned status ${reply.status}.`);
  }

  private nextMessageNo(): string {
    this.messageCounter += 1;
    return String(this.messageCounter);
  }

  private async request(
    command: string,
    channelId: string,
    groupNo: string = DEFAULT_GROUP_NO,
    operation: string = DEFAULT_OPERATION,
    value: string = '',
    timeoutMs = 5000,
    // What to show in the Diagnostics event log for this send, when it
    // differs from the actual wire value - only sendMessage/
    // sendTemplateSignal need this (their wire value has a message id
    // prepended via wire.ts's withMessageId that shouldn't leak into the
    // displayed sentence - see their own comments). Defaults to value.
    diagnosticValue: string = value
  ): Promise<CueReply> {
    await this.connect();
    if (!this.ws) throw new CueClientError('Not connected to the Cue server.');

    const messageNo = this.nextMessageNo();
    const plaintext = buildPayload({ messageNo, command, channelId, groupNo, operation, value });
    const base64Payload = await encryptBase64(this.secret, plaintext);
    const envelope = buildEnvelope(this.securityKey, base64Payload);
    // appLog(`[Cue] -> ${command} ${channelId}.${groupNo} (messageNo=${messageNo})`);
    // channelId is the heartbeat's "_" sentinel for su - omit it there so
    // describeCueEvent doesn't say "Sent heartbeat to _".
    recordCueEvent({ direction: 'out', kind: 'command', command, channelId: channelId === '_' ? undefined : channelId, value: diagnosticValue || undefined });

    return new Promise<CueReply>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(messageNo);
        appWarn(`[Cue] <- timeout waiting for reply to ${command} ${channelId}.${groupNo} (messageNo=${messageNo}), ws.readyState=${this.ws?.readyState}`);
        recordCueEvent({ direction: 'in', kind: 'timeout', command, channelId: channelId === '_' ? undefined : channelId });
        reject(new CueClientError('Timed out waiting for a reply from the Cue server.'));
      }, timeoutMs);
      // Registering the pending reply before the socket send is race-free
      // against the message handler processing a very fast response, since
      // WebSocket.send is synchronous from the caller's point of view.
      this.pending.set(messageNo, { resolve, reject, timer, command, channelId, sentAt: Date.now() });
      this.ws!.send(envelope);
    });
  }

  private async handleIncoming(text: string): Promise<void> {
    const decrypted = await this.decryptIncoming(text);
    if (decrypted === null) {
      appWarn('[Cue] <- received a frame that could not be decrypted/decoded as plaintext, dropping:', text);
      recordCueEvent({ direction: 'in', kind: 'error', note: 'Received a frame that could not be decrypted' });
      return;
    }
    const incoming = parseIncoming(decrypted);
    if (!incoming) {
      appWarn('[Cue] <- decrypted a frame but could not parse it as a reply or relay, dropping:', decrypted);
      recordCueEvent({ direction: 'in', kind: 'error', note: 'Received a frame that could not be understood' });
      return;
    }

    if (incoming.kind === 'reply') {
      const pending = this.pending.get(incoming.reply.messageNo);
      if (pending) {
        clearTimeout(pending.timer);
        this.pending.delete(incoming.reply.messageNo);
        const rttMs = Date.now() - pending.sentAt;
        recordCueEvent({ direction: 'in', kind: 'reply', command: pending.command, status: incoming.reply.status, rttMs });
        if (pending.command === CueCommand.sessionHeartbeat) recordCueHeartbeatAck(rttMs);
        pending.resolve(incoming.reply);
      } else {
        // A reply for a messageNo nothing is waiting on - either it arrived
        // after request() already timed out and gave up, or the counters
        // never matched up in the first place (e.g. a stale reply from a
        // previous connection). Either way, worth seeing rather than
        // silently discarding.
        appWarn(`[Cue] <- reply for messageNo=${incoming.reply.messageNo} (status=${incoming.reply.status}) has no matching pending request`);
      }
      return;
    }
    // appLog(`[Cue] <- relay ${incoming.relayed.command} ${incoming.relayed.channelId}.${incoming.relayed.groupNo}`);
    recordCueEvent({ direction: 'in', kind: 'relay', command: incoming.relayed.command, channelId: incoming.relayed.channelId });
    for (const handler of this.relayHandlers) {
      try {
        handler(incoming.relayed);
      } catch (error) {
        appWarn('[Cue] a relay handler threw', error);
      }
    }
  }

  private async decryptIncoming(text: string): Promise<string | null> {
    if (isPlaintextStatusReply(text)) return text;
    try {
      return await decryptBase64(this.secret, text);
    } catch (error) {
      appWarn('[Cue] decrypt failed for an incoming frame:', error);
      return null;
    }
  }
}
